import { useMemo } from 'react';
import { campaignCopy } from '@discovr/features';
import type { CampaignPipeline, Post, RosterEntryFinalized } from '@discovr/types';
import type { ColumnDef } from '../../components/common/DataTable';
import { ConfirmedRowMoreMenu } from './ConfirmedRowMoreMenu';
import { mainPost } from './ConfirmedTab.helpers';
import { holdBlockedReason, rollbackMenuBlockedReason, rollbackTarget } from './ConfirmedTab.hold';

const holdCopy = campaignCopy.creator.hold;

export const ROW_MENU_COLUMN_ID = 'row_menu';

const ACTION_CELL_PADDING = 'px-2 @4xl/dt:px-2 @6xl/dt:px-2';

type RowOpener = (row: RosterEntryFinalized, opener?: HTMLElement | null) => void;

interface RowMenuColumnOptions {
  enabled: boolean;
  canManageHold: boolean;
  canRollback: boolean;
  pipeline: CampaignPipeline | null;
  isRowHeld: (post?: Post) => boolean;
  isHoldWired: boolean;
  isReleaseWired: boolean;
  isRollbackWired: boolean;
  isAcceptanceRollbackWired: boolean;
  openHold: RowOpener;
  openRelease: RowOpener;
  openRollback: RowOpener;
}

// Tints every cell while this row's menu is open. The mix is opaque so pinned
// cells still cover what scrolls beneath them, and it hides the shared row's
// translucent aria-expanded tint. Literal classes so Tailwind's scanner sees them.
export const ROW_MENU_OPEN_ROW_CLASS =
  '[&>td]:transition-colors [&>td]:duration-150 [&>td]:ease-out motion-reduce:transition-none! motion-reduce:[&>td]:transition-none! [&:has([data-row-menu-trigger][data-state=open])>td]:bg-[color-mix(in_oklab,var(--muted)_60%,var(--card))]';

export function useConfirmedRowMenuColumn({
  enabled,
  canManageHold,
  canRollback,
  pipeline,
  isRowHeld,
  isHoldWired,
  isReleaseWired,
  isRollbackWired,
  isAcceptanceRollbackWired,
  openHold,
  openRelease,
  openRollback,
}: RowMenuColumnOptions): ColumnDef<RosterEntryFinalized> | null {
  return useMemo(() => {
    if (!enabled) return null;
    return {
      id: ROW_MENU_COLUMN_ID,
      header: 'Action',
      alwaysVisible: true,
      // The 36px trigger plus cell padding; also fits the uppercase header.
      minWidth: 72,
      maxWidth: 72,
      // Fixed tight padding at every container tier so "ACTION" never overflows 72px.
      headerClassName: ACTION_CELL_PADDING,
      cellClassName: ACTION_CELL_PADDING,
      align: 'center',
      sticky: 'right',
      cell: (c) => {
        const post = mainPost(c);
        const rollback = canRollback ? rollbackTarget(post, pipeline) : null;
        return (
          <ConfirmedRowMoreMenu
            canHold={canManageHold}
            isHeld={canManageHold && isRowHeld(post)}
            blockedReason={isHoldWired ? holdBlockedReason(post) : holdCopy.actionUnavailable}
            releaseBlockedReason={isReleaseWired ? null : holdCopy.actionUnavailable}
            onHold={(trigger) => openHold(c, trigger)}
            onRelease={(trigger) => openRelease(c, trigger)}
            rollback={
              rollback && {
                label: rollback.labels.menu,
                blockedReason: rollbackMenuBlockedReason(
                  post,
                  rollback.kind === 'acceptance' ? isAcceptanceRollbackWired : isRollbackWired,
                ),
                onSelect: (trigger) => openRollback(c, trigger),
              }
            }
          />
        );
      },
    };
  }, [enabled, canManageHold, canRollback, pipeline, isRowHeld, isHoldWired, isReleaseWired, isRollbackWired, isAcceptanceRollbackWired, openHold, openRelease, openRollback]);
}

export function withTrailingColumn<T>(columns: ColumnDef<T>[], trailing: ColumnDef<T> | null): ColumnDef<T>[] {
  return trailing ? [...columns, trailing] : columns;
}
